import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Video, Brain, Trophy, Layers, Utensils, Dumbbell, Bot } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const slides = [
  {
    icon: Layers,
    color: 'from-orange-500 to-red-500',
    title: 'Your All-in-One Wellness Hub',
    subtitle: 'Everything you need in one app',
    points: [
      'Nutrition tracking + Exercise coaching',
      'Meal planning + Social challenges',
      'Progress photos + Wearable sync',
    ],
    accent: 'No more switching between 5 different apps',
  },
  {
    icon: Bot,
    color: 'from-blue-500 to-purple-600',
    title: '24/7 AI Coaching',
    subtitle: 'Your personal nutritionist & trainer — always online',
    points: [
      'Ask any nutrition question, anytime',
      'Get personalized exercise guidance',
      'AI analyzes your meals from photos',
    ],
    accent: 'Like having a coach in your pocket',
  },
  {
    icon: Brain,
    color: 'from-green-500 to-teal-500',
    title: 'Smart Meal Planning',
    subtitle: 'AI plans your meals with your budget in mind',
    points: [
      'Vegan, gluten-free, organic options',
      'Budget-aware — fits your wallet',
      'Regional pricing for your local stores',
    ],
    accent: 'Eat healthy without breaking the bank',
  },
  {
    icon: Video,
    color: 'from-purple-500 to-pink-500',
    title: 'AI Video Studio',
    subtitle: 'Generate workout & recipe videos with AI',
    points: [
      'Create exercise how-to videos',
      'Generate recipe prep content',
      'Share on social media',
    ],
    accent: 'Powered by Sora 2 — only on NutriFitness AI',
  },
  {
    icon: Trophy,
    color: 'from-amber-500 to-orange-500',
    title: 'Stay Motivated',
    subtitle: 'Gamification that keeps you going',
    points: [
      'Earn badges for every milestone',
      'Compete in social challenges',
      'Track streaks and level up',
    ],
    accent: "Let's start your journey!",
  },
];

export default function OnboardingTutorial({ onComplete, isReplay = false }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  const slide = slides[current];
  const Icon = slide.icon;
  const isLast = current === slides.length - 1;

  const goNext = () => {
    if (isLast) { onComplete(); return; }
    setDirection(1);
    setCurrent(c => c + 1);
  };

  const goPrev = () => {
    if (current === 0) return;
    setDirection(-1);
    setCurrent(c => c - 1);
  };

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (diff > 50) goNext();
    else if (diff < -50) goPrev();
    setTouchStart(null);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onComplete();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [current]);

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="onboarding-tutorial"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl relative">
        {/* Close button */}
        <button
          onClick={onComplete}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white dark:bg-gray-800/20 hover:bg-white dark:bg-gray-800/40 text-white transition-colors"
          data-testid="onboarding-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header gradient */}
        <div className={`bg-gradient-to-br ${slide.color} p-8 pb-12 text-center relative`}>
          <div className="w-16 h-16 bg-white dark:bg-gray-800/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">{slide.title}</h2>
          <p className="text-white/80 text-sm">{slide.subtitle}</p>
        </div>

        {/* Content */}
        <div className="p-6 -mt-6">
          <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-5 space-y-3">
            {slide.points.map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${slide.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <span className="text-white text-xs font-bold">{i + 1}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-200">{point}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3 italic">{slide.accent}</p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          {/* Dots */}
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
                className={`h-2 rounded-full transition-all ${i === current ? 'w-6 bg-gradient-to-r ' + slide.color : 'w-2 bg-gray-300 dark:bg-gray-600'}`}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div className="flex gap-2">
            {current > 0 && (
              <button
                onClick={goPrev}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
                data-testid="onboarding-prev-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={goNext}
              className={`px-5 py-2 rounded-lg bg-gradient-to-r ${slide.color} text-white font-semibold text-sm flex items-center gap-1 hover:opacity-90 transition-opacity`}
              data-testid="onboarding-next-btn"
            >
              {isLast ? "Let's Go!" : 'Next'}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
